import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import { googleBusinessCategories } from '../../../utils/googleBusinessCategories';

interface CategoryAutocompleteProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (category: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
}

interface CategoryItem {
  category: string;
  isCustom: boolean;
}

const ITEM_HEIGHT = 44;
const MAX_VISIBLE_ITEMS = 10;
const MAX_RESULTS = 100;

const CategoryAutocomplete: React.FC<CategoryAutocompleteProps> = ({
  id,
  name,
  value,
  onChange,
  placeholder = "Search for a category...",
  className = "",
  error,
  disabled = false,
  ariaDescribedBy,
  ariaInvalid,
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<List>(null);

  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredCategories = useMemo(() => {
    if (!searchTerm || searchTerm.length < 1) {
      return [];
    }

    const query = searchTerm.toLowerCase().trim();
    const matches: CategoryItem[] = [];

    for (const category of googleBusinessCategories) {
      if (category.toLowerCase().includes(query)) {
        matches.push({ category, isCustom: false });
        if (matches.length >= MAX_RESULTS) {
          break;
        }
      }
    }

    const exactMatch = matches.some(item => item.category.toLowerCase() === query);
    if (!exactMatch && query.length >= 2) {
      matches.push({ category: searchTerm, isCustom: true });
    }

    return matches;
  }, [searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    onChange(newValue);
    setShowDropdown(true);
    setHighlightedIndex(0);
  };

  const handleCategorySelect = useCallback((item: CategoryItem) => {
    setSearchTerm(item.category);
    onChange(item.category);
    setShowDropdown(false);
    setHighlightedIndex(0);
  }, [onChange]);

  const handleInputFocus = () => {
    if (disabled) return;
    if (searchTerm && filteredCategories.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || filteredCategories.length === 0) {
      if (e.key === 'Enter' && searchTerm.trim()) {
        onChange(searchTerm.trim());
        setShowDropdown(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredCategories.length - 1 ? prev + 1 : prev
        );
        if (listRef.current) {
          const nextIndex = highlightedIndex < filteredCategories.length - 1 
            ? highlightedIndex + 1 
            : highlightedIndex;
          listRef.current.scrollToItem(nextIndex, 'smart');
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        if (listRef.current) {
          const nextIndex = highlightedIndex > 0 ? highlightedIndex - 1 : 0;
          listRef.current.scrollToItem(nextIndex, 'smart');
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCategories[highlightedIndex]) {
          handleCategorySelect(filteredCategories[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        break;
    }
  };

  const Row = useCallback(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = filteredCategories[index];
    if (!item) return null;

    return (
      <div
        style={style}
        className={`px-4 py-2.5 cursor-pointer text-sm md:text-base transition-colors duration-150 ${
          highlightedIndex === index
            ? 'bg-blue-50 text-blue-900 border-l-4 border-blue-500 font-medium'
            : 'hover:bg-gray-50'
        } ${item.isCustom ? 'font-medium text-blue-600' : 'text-gray-700'}`}
        onClick={() => handleCategorySelect(item)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        {item.isCustom ? (
          <span>Use custom: "{item.category}"</span>
        ) : (
          <span>{item.category}</span>
        )}
      </div>
    );
  }, [filteredCategories, highlightedIndex, handleCategorySelect]);

  const listHeight = Math.min(
    filteredCategories.length * ITEM_HEIGHT,
    MAX_VISIBLE_ITEMS * ITEM_HEIGHT
  );

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        name={name}
        ref={inputRef}
        type="text"
        disabled={disabled}
        className={`w-full px-4 py-2.5 text-sm md:text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${
          error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
        } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : ''}`}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
      />

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      {!disabled && showDropdown && filteredCategories.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1.5 bg-white rounded-lg border border-gray-200 shadow-md"
        >
          <List
            ref={listRef}
            height={listHeight}
            itemCount={filteredCategories.length}
            itemSize={ITEM_HEIGHT}
            width="100%"
          >
            {Row}
          </List>
        </div>
      )}

      {!disabled && showDropdown && filteredCategories.length === 0 && searchTerm && (
        <div
          ref={dropdownRef}
          className="absolute z-10 w-full mt-1.5 bg-white rounded-lg border border-gray-200 shadow-md"
        >
          <div className="px-4 py-3 text-sm text-gray-500">
            No categories found. Press Enter to use "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryAutocomplete;
